import { z } from 'zod';
/**
 * Advanced filters: "invoices where customer is Acme and balance is over
 * ₹10,000 and due date is before today", built condition by condition, and
 * the saved filters a book reuses.
 *
 * ── WHY A CONDITION LIST AND NOT MORE QUERY PARAMS ──
 * Every list already takes a handful of named parameters - `status`,
 * `customerId`, `from`/`to` - and each new question ("expenses over ₹5,000
 * with no receipt", "invoices due this week that are part-paid") would be
 * one more parameter with one more meaning to learn. A condition is
 * `{ field, operator, value }` against a catalogue the API publishes, so the
 * question is spelt the way a person asks it and a new field is one line in
 * the catalogue rather than a new parameter on three screens.
 *
 * A filter belongs to exactly one record type. An invoice has a due date
 * and no category; an expense has a category and no customer balance. The
 * catalogue is served per type and a saved filter is only ever offered on
 * the list it was built on.
 */
export const FILTER_ENTITY_TYPES = ['invoice', 'expense', 'payment', 'customer', 'transaction'];
export const filterEntityTypeSchema = z.enum(FILTER_ENTITY_TYPES);
/**
 * Every operator the builder knows. Which ones a field offers is decided by
 * that field's type on the server (`common/record-filters.ts`); listing them
 * all in one enum keeps the wire format flat and lets the client render
 * whatever the catalogue says is allowed.
 */
export const filterOperatorSchema = z.enum([
    // text
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    // equality - text, number, date, boolean
    'eq',
    'neq',
    // choice
    'in',
    'not_in',
    // number and date ordering
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    // multi-value membership - tags
    'has_any',
    'has_all',
    // presence. Deliberately separate from `eq ''`: a NULL and an empty string
    // are different states in every one of these tables, and a person picking
    // "is empty" means "nobody filled this in".
    'is_empty',
    'is_not_empty',
]);
/**
 * One condition. `field` is a key from the entity's catalogue. `value` is
 * unknown on the wire and narrowed per operator and per field type on the
 * server, which has to re-derive the operator/type matrix from the catalogue
 * anyway - typing it here would encode that matrix twice.
 */
export const filterConditionSchema = z
    .object({
    field: z.string().trim().min(1).max(80),
    operator: filterOperatorSchema,
    value: z.unknown().optional(),
})
    .strict();
/**
 * A whole filter: a flat list of conditions joined by one conjunction.
 *
 * Not a nested tree. Arbitrary AND/OR nesting is a much larger UI and a much
 * larger builder, and every filter anyone has asked for is a flat list. If
 * nesting is needed later this shape becomes one node of it without changing
 * what is already saved.
 */
export const filterDefinitionSchema = z
    .object({
    conjunction: z.enum(['and', 'or']).default('and'),
    conditions: z.array(filterConditionSchema).max(25),
})
    .strict();
/**
 * How a filter travels on a list request: as JSON in the `where` query
 * param, because the lists are GETs and a filter is a structure.
 *
 * `where`, not `filter`, because the customer list already has a `filter`
 * (`all` / `receivable` / `overdue`) and one name meaning two things on two
 * lists is exactly the confusion a shared grammar is meant to remove.
 *
 * A malformed value is a 400, never a silently unfiltered list - which would
 * show a person every record when they asked for six.
 */
export const FILTER_QUERY_PARAM = 'where';
const MAX_FILTER_PARAM_LENGTH = 8000;
/**
 * The `where` param as a list schema spreads it in: the JSON string the URL
 * carries, or the object itself (the MCP tools and tests pass structures),
 * parsed and validated into one `FilterDefinition`.
 */
export const filterParamSchema = z
    .union([z.string().max(MAX_FILTER_PARAM_LENGTH), filterDefinitionSchema])
    .transform((raw, ctx) => {
    if (typeof raw !== 'string')
        return raw;
    if (raw.trim() === '')
        return undefined;
    let decoded;
    try {
        decoded = JSON.parse(raw);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'The filter could not be read. Rebuild it and try again.' });
        return z.NEVER;
    }
    const parsed = filterDefinitionSchema.safeParse(decoded);
    if (!parsed.success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'That filter is not in a shape this list understands.' });
        return z.NEVER;
    }
    return parsed.data;
});
/**
 * The two keys every filterable list schema spreads in. One or the other:
 * the UI sends an ad-hoc `where` or a `savedFilterId`, never both, and when
 * both arrive the named, shared thing wins.
 */
export const advancedFilterQueryFragment = {
    [FILTER_QUERY_PARAM]: filterParamSchema.optional(),
    savedFilterId: z.string().trim().max(36).optional(),
};
/**
 * Saved filters are book-level: anyone who can view the record type sees
 * them and can apply them. `createdBy` is kept so the list can say whose
 * filter it is, and so changing it is limited to the author or an admin.
 */
export const createSavedFilterSchema = z
    .object({
    entityType: filterEntityTypeSchema,
    name: z.string().trim().min(1).max(120),
    definition: filterDefinitionSchema,
})
    .strict();
export const updateSavedFilterSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    definition: filterDefinitionSchema.optional(),
})
    .strict();
/**
 * Field types the filter UI knows how to render an input for. `select` is
 * a fixed or live choice list (a status, a customer); `multiselect` is
 * membership in a set the record can hold several of (tags).
 */
export const FILTER_FIELD_TYPES = ['text', 'number', 'date', 'boolean', 'select', 'multiselect'];
export const filterFieldTypeSchema = z.enum(FILTER_FIELD_TYPES);
/**
 * Which live list a `select` field's options come from. Resolved on the
 * server when the catalogue is served, so the list a person picks from is
 * the list the server will match against.
 */
export const FILTER_OPTION_SOURCES = [
    'customers',
    'vendors',
    'projects',
    'tags',
    'accounts',
    'expenseCategories',
    'incomeCategories',
    'members',
    'items',
];
/**
 * How the sheet words each operator. Ordering words read differently for a
 * number ("is more than") than for a date ("is after"), which is why the
 * label is looked up with the field type.
 */
export function filterOperatorLabel(operator, type) {
    if (type === 'number') {
        switch (operator) {
            case 'gt':
                return 'is more than';
            case 'gte':
                return 'is at least';
            case 'lt':
                return 'is less than';
            case 'lte':
                return 'is at most';
            default:
                break;
        }
    }
    switch (operator) {
        case 'contains':
            return 'contains';
        case 'not_contains':
            return 'does not contain';
        case 'starts_with':
            return 'starts with';
        case 'ends_with':
            return 'ends with';
        case 'eq':
            return 'is';
        case 'neq':
            return 'is not';
        case 'in':
            return 'is any of';
        case 'not_in':
            return 'is none of';
        case 'gt':
            return 'is after';
        case 'gte':
            return 'is on or after';
        case 'lt':
            return 'is before';
        case 'lte':
            return 'is on or before';
        case 'between':
            return 'is between';
        case 'has_any':
            return 'has any of';
        case 'has_all':
            return 'has all of';
        case 'is_empty':
            return 'is empty';
        case 'is_not_empty':
            return 'is not empty';
    }
}
/** The operators that take no value. */
export const VALUELESS_OPERATORS = ['is_empty', 'is_not_empty'];
//# sourceMappingURL=record-filters.js.map